import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import { useGlobalEntity } from '../hooks/useGlobalEntity';

const API = `${API_V1}/sox404`;

// ── Types ──────────────────────────────────────────────────────

interface ControlTest {
  test_code: string;
  test_name: string;
  cycle: string;
  component: string;
  control_type: string;
  automation_level: string;
  frequency: string;
  key_control: boolean;
  asc_paragraph: string | null;
  coso_principle: string | null;
  attestation_column: string | null;
  hard_block_code: string | null;
  description: string;
  test_procedure: string;
}

interface ControlMatrix {
  total: number;
  by_component: Record<string, number>;
  by_control_type: Record<string, number>;
  key_control_count: number;
  tests: ControlTest[];
  asc_paragraph: string;
}

interface EvidenceResult {
  test: ControlTest;
  window: { from: string; to: string; months: number };
  sample_size: number;
  in_scope: number;
  pass_count: number;
  fail_count: number;
  not_performed: number;
  pass_rate: number | null;
  severity: string;
  evidence_note: string;
  thresholds: Record<string, number>;
}

interface Deficiencies {
  tests_evaluated: number;
  severity_counts: Record<string, number>;
  key_control_severity_counts: Record<string, number>;
  key_control_deficiencies: string[];
  overall_icfr_conclusion: string;
  results: EvidenceResult[];
  as_of: string;
  period_months: number;
}

interface ManagementAssertion {
  conclusion_label: string;
  preamble: string;
  narrative: string;
  framework: string;
  section_reference: string;
}

interface SOX404Report {
  as_of: string;
  period_months: number;
  cycle: string | null;
  overall_icfr_conclusion: string;
  executive_summary: {
    overall_conclusion: string;
    total_controls_in_catalog: number;
    tests_evaluated: number;
    key_control_deficiency_count: number;
    severity_counts: Record<string, number>;
    key_control_severity_counts: Record<string, number>;
  };
  control_matrix: ControlMatrix;
  deficiency_list: EvidenceResult[];
  management_assertion: ManagementAssertion;
  disclosure_note: string;
}

// ── Styling helpers ────────────────────────────────────────────

const severityColor = (sev: string): string => {
  switch (sev) {
    case 'MATERIAL_WEAKNESS': return 'var(--accent-critical)';
    case 'SIGNIFICANT_DEFICIENCY': return 'var(--warning)';
    case 'CONTROL_DEFICIENCY': return '#d97706';
    case 'OPERATING_EFFECTIVELY': return 'var(--positive)';
    case 'NOT_TESTED': return 'var(--text-hint)';
    default: return 'var(--text-secondary)';
  }
};

const conclusionLabel = (conclusion: string): string => {
  switch (conclusion) {
    case 'EFFECTIVE': return 'ICFR EFFECTIVE';
    case 'EFFECTIVE_WITH_SCOPE_LIMITATION': return 'EFFECTIVE (scope limited)';
    case 'CONTROL_DEFICIENCIES': return 'CONTROL DEFICIENCIES';
    case 'SIGNIFICANT_DEFICIENCIES': return 'SIGNIFICANT DEFICIENCIES';
    case 'MATERIAL_WEAKNESS': return 'MATERIAL WEAKNESS';
    case 'NO_CONTROLS_CONFIGURED': return 'NO CONTROLS';
    default: return conclusion;
  }
};

const conclusionColor = (conclusion: string): string => {
  switch (conclusion) {
    case 'EFFECTIVE': return 'var(--positive)';
    case 'EFFECTIVE_WITH_SCOPE_LIMITATION': return 'var(--warning)';
    case 'CONTROL_DEFICIENCIES': return '#d97706';
    case 'SIGNIFICANT_DEFICIENCIES': return 'var(--warning)';
    case 'MATERIAL_WEAKNESS': return 'var(--accent-critical)';
    default: return 'var(--text-secondary)';
  }
};

const severityBadge = (sev: string) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
    background: `${severityColor(sev)}22`, color: severityColor(sev),
    fontSize: 11, fontWeight: 600,
  }}>{sev.replace(/_/g, ' ')}</span>
);

const csvEscape = (val: any): string => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCsv = (filename: string, rows: any[], columns: { key: string; label: string }[]) => {
  const header = columns.map((c) => csvEscape(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => csvEscape(r[c.key])).join(',')).join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
};

// ── Columns ────────────────────────────────────────────────────

const matrixCols: Column[] = [
  { key: 'test_code', label: 'Code', render: (v: string) => <span style={{ fontWeight: 600, fontSize: 11 }}>{v}</span> },
  { key: 'test_name', label: 'Control' },
  { key: 'component', label: 'Comp', render: (v: string) => <span style={{ fontSize: 11 }}>{v}</span> },
  { key: 'control_type', label: 'Type', render: (v: string) => <span style={{ fontSize: 11 }}>{v === 'PREVENTIVE' ? 'PREV' : 'DET'}</span> },
  { key: 'automation_level', label: 'Auto', render: (v: string) => <span style={{ fontSize: 11 }}>{v.replace('SEMI_', '½')}</span> },
  { key: 'key_control', label: 'Key', render: (v: boolean) => v ? <span style={{ color: 'var(--accent)', fontWeight: 700 }}>KEY</span> : '' },
  { key: 'coso_principle', label: 'COSO' },
  { key: 'asc_paragraph', label: 'ASC Ref', render: (v: string) => v ? <span style={{ fontSize: 11 }}>{v}</span> : '—' },
];

// ── Component ──────────────────────────────────────────────────

export default function SOX404ReportView() {
  const { entity } = useGlobalEntity();
  const [tab, setTab] = useState('exec');
  const [periodMonths, setPeriodMonths] = useState(12);
  const [report, setReport] = useState<SOX404Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`${API}/report?period_months=${periodMonths}`);
      if (r.ok) {
        setReport(await r.json());
      } else {
        const err = await r.json();
        setError(err.detail || `HTTP ${r.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    }
    setLoading(false);
  }, [periodMonths]);

  useEffect(() => { load(); }, [load]);

  const tabs = [
    { key: 'exec', label: 'Executive Summary' },
    { key: 'matrix', label: 'Control Matrix' },
    { key: 'deficiencies', label: 'Deficiencies' },
    { key: 'assertion', label: 'Management Assertion' },
  ];

  if (loading) {
    return (
      <PageLayout title="SOX 404 Report" subtitle="ICFR assessment per SOX 404(a) + PCAOB AS 2201">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading…</div>
      </PageLayout>
    );
  }

  if (error || !report) {
    return (
      <PageLayout title="SOX 404 Report" subtitle="ICFR assessment per SOX 404(a) + PCAOB AS 2201">
        <div style={{ padding: 16, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.30)', borderRadius: 6, color: 'var(--accent-critical)', fontSize: 13 }}>
          Error: {error || 'No report available'}
        </div>
      </PageLayout>
    );
  }

  const conclusion = report.overall_icfr_conclusion;
  const sev = report.executive_summary.severity_counts;
  const keySev = report.executive_summary.key_control_severity_counts;

  return (
    <PageLayout
      title="SOX 404 Report"
      subtitle="ICFR assessment per SOX Section 404(a) + PCAOB AS 2201 + COSO 2013"
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Period window:</label>
        <select
          aria-label="Period window months"
          value={periodMonths}
          onChange={(e) => setPeriodMonths(parseInt(e.target.value))}
          style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value={3}>3 months</option>
          <option value={6}>6 months</option>
          <option value={12}>12 months (default)</option>
          <option value={24}>24 months</option>
          <option value={36}>36 months</option>
        </select>
        <button
          onClick={load}
          style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}
        >Refresh</button>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
          As of: <strong>{report.as_of.split('T')[0]}</strong>
        </div>
      </div>

      {/* Overall conclusion banner */}
      <div style={{
        padding: '14px 18px', marginBottom: 20, borderRadius: 6,
        background: `${conclusionColor(conclusion)}18`,
        border: `1px solid ${conclusionColor(conclusion)}44`,
      }}>
        <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 4 }}>OVERALL ICFR CONCLUSION (AS 2201 ¶69)</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: conclusionColor(conclusion) }}>
          {conclusionLabel(conclusion)}
        </div>
      </div>

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'exec' && (
        <>
          <KpiGrid columns={4} items={[
            { label: 'Controls', value: report.executive_summary.total_controls_in_catalog },
            { label: 'Tests Evaluated', value: report.executive_summary.tests_evaluated },
            { label: 'Key Ctrl. Deficiencies', value: report.executive_summary.key_control_deficiency_count, color: report.executive_summary.key_control_deficiency_count > 0 ? 'var(--warning)' : 'var(--positive)' },
            { label: 'Window (months)', value: report.period_months },
          ]} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div style={{ padding: 14, border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>All Controls — Severity Breakdown</div>
              {Object.entries(sev).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 12 }}>
                  <span>{severityBadge(k)}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: 14, border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Key Controls — Severity Breakdown</div>
              {Object.entries(keySev).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 12 }}>
                  <span>{severityBadge(k)}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ padding: '10px 14px', borderRadius: 6, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.20)', color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.5 }}>
            <strong>Disclosure note:</strong> {report.disclosure_note}
          </div>
        </>
      )}

      {tab === 'matrix' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {report.control_matrix.total} controls: {report.control_matrix.by_control_type.DETECTIVE || 0} detective /
              {' '}{report.control_matrix.by_control_type.PREVENTIVE || 0} preventive —
              {' '}{report.control_matrix.key_control_count} key
            </div>
            <button
              onClick={() => downloadCsv('sox404-control-matrix.csv', report.control_matrix.tests, matrixCols.map((c) => ({ key: c.key, label: c.label })))}
              style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}
            >Download CSV</button>
          </div>
          <DataTable columns={matrixCols} rows={report.control_matrix.tests} rowKey="test_code" emptyMessage="No controls configured." />
        </>
      )}

      {tab === 'deficiencies' && (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
            Showing {report.deficiency_list.length} control(s) with severity less than OPERATING_EFFECTIVELY.
          </div>
          {report.deficiency_list.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--positive)', border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)' }}>
              ✓ No deficiencies detected in the {report.period_months}-month window.
            </div>
          ) : (
            report.deficiency_list.map((d) => (
              <div key={d.test.test_code} style={{ marginBottom: 10, padding: 14, border: '1px solid var(--border-subtle)', borderRadius: 6, background: 'var(--bg-card)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-hint)', fontWeight: 600 }}>{d.test.test_code}</div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{d.test.test_name}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {d.test.key_control && <span style={{ color: 'var(--accent)', fontSize: 10, fontWeight: 700 }}>KEY</span>}
                    {severityBadge(d.severity)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 20, fontSize: 12, marginBottom: 8 }}>
                  <span>Sample: <strong>{d.sample_size}</strong></span>
                  <span>In-scope: <strong>{d.in_scope}</strong></span>
                  <span>Pass: <strong style={{ color: 'var(--positive)' }}>{d.pass_count}</strong></span>
                  <span>Fail: <strong style={{ color: 'var(--accent-critical)' }}>{d.fail_count}</strong></span>
                  <span>Not performed: <strong>{d.not_performed}</strong></span>
                  {d.pass_rate !== null && <span>Pass rate: <strong>{(d.pass_rate * 100).toFixed(1)}%</strong></span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: 8, background: 'var(--bg-hover)', borderRadius: 4, lineHeight: 1.5 }}>
                  {d.evidence_note}
                </div>
                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-hint)' }}>
                  {d.test.asc_paragraph} · COSO {d.test.coso_principle} · {d.test.control_type.toLowerCase()}
                </div>
              </div>
            ))
          )}
        </>
      )}

      {tab === 'assertion' && (
        <div style={{ padding: 20, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 6 }}>
            {report.management_assertion.section_reference} · {report.management_assertion.framework}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 16, color: 'var(--text-primary)' }}>
            {report.management_assertion.preamble}
          </div>
          <div style={{ padding: 14, borderRadius: 6, background: `${conclusionColor(conclusion)}12`, border: `1px solid ${conclusionColor(conclusion)}33`, fontSize: 14, lineHeight: 1.7, color: 'var(--text-primary)' }}>
            {report.management_assertion.narrative}
          </div>
          <div style={{ marginTop: 16, fontSize: 11, color: 'var(--text-hint)', lineHeight: 1.5 }}>
            This narrative is machine-generated from the live control catalog + evidence rollup. Reviewer sign-off is required at period close before external filing. Not a substitute for SOX 404(b) auditor attestation.
          </div>
        </div>
      )}
    </PageLayout>
  );
}
